import {
    chakra,
    forwardRef,
    omitThemingProps,
    useFormControl,
    useMergeRefs,
    usePopper,
} from '@chakra-ui/react';
import React, { Children, cloneElement, isValidElement, useMemo } from 'react';

import { useSelect } from './useSelect';
import { useMultiStyleConfig } from './useMultiStyleConfig';
import { mergeWith } from './mergeWith';

/**
 * @typedef {Object} OptionProps
 * @property {string} value - The value of the option
 * @property {React.ReactNode} [children] - The content to display (defaults to value if not provided)
 */

/**
 *
 * @type {React.ForwardRefExoticComponent<OptionProps & import('@chakra-ui/react').HTMLChakraProps<'li'> & import('@chakra-ui/react').ThemingProps>}
 * */
const Option = forwardRef((props, ref) => {
    const { children, value, ...rest } = omitThemingProps(props);
    const { option } = useMultiStyleConfig('CustomSelect', props);
    return (
        <chakra.li ref={ref} __css={option} {...rest}>
            {children || value}
        </chakra.li>
    );
});

/**
 * @param {import('react').ComponentProps<'svg'>} props
 * @returns {React.ReactElement} SVG Element
 * */
const DefaultIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path
            fill="currentColor"
            d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"
        />
    </svg>
);

/**
 * @param {import('react').ComponentProps<'svg'>} props
 * @returns {React.ReactElement} SVG Element
 * */
const SelectIcon = (props) => (
    <DefaultIcon
        style={{
            fontSize: '20px',
            width: '1em',
            height: '1em',
            color: 'currentColor',
            transform: `rotate(${props.isOpen ? -180 : 0}deg)`,
            transitionDuration: '.4s',
        }}
        role="presentation"
        focusable={false}
        aria-hidden
        {...props}
    />
);

/**
 * @typedef SelectProps
 * @property {string} [placeholder]
 * @property {string | null | undefined} [value]
 * @property {(item: string | null | undefined) => void} [onChange]
 * @property {React.ReactNode} [children]
 * */

/**
 * @type {React.ForwardRefExoticComponent<SelectProps & import('@chakra-ui/react').FormControlOptions & import('@chakra-ui/react').ThemingProps<'Select'> & Omit<import('@chakra-ui/react').HTMLChakraProps<'button'>, 'disabled' | 'required' | 'readOnly' | 'size' | 'value' | 'onChange'>>}
 * */
const CustomSelect = React.forwardRef((props, ownRef) => {
    const { id, value, children, placeholder, onChange, ...rest } =
        omitThemingProps(props);
    const ownButtonProps = useFormControl(rest);
    const styles = useMultiStyleConfig('CustomSelect', props);

    const validChildren = useMemo(
        () =>
            Children.toArray(children)
                .filter(isValidElement)
                .filter((child) => 'value' in child.props),
        [children],
    );

    const items = validChildren.map((child) => child.props.value);
    const {
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
    } = useSelect({
        id,
        items,
        selectedItem: value,
        onSelectedItemChange: (val) => onChange?.(val.selectedItem),
    });

    const { referenceRef: popperRef, getPopperProps } = usePopper({
        enabled: isOpen,
        gutter: 2,
    });
    const { ref: useSelectToggleButtonRef, ...useSelectToggleButtonProps } =
        getToggleButtonProps();

    const toggleButtonRef = useMergeRefs(
        ownRef,
        useSelectToggleButtonRef,
        popperRef,
    );
    const toggleButtonProps = mergeWith(
        ownButtonProps,
        useSelectToggleButtonProps,
    );

    return (
        <chakra.div position="relative">
            <chakra.button
                ref={toggleButtonRef}
                __css={styles.field}
                data-focus-visible-added={isOpen}
                {...toggleButtonProps}>
                {validChildren.find(
                    (child) => child.props.value === selectedItem,
                )?.props.children ||
                    selectedItem || <chakra.span>{placeholder}</chakra.span>}
                <SelectIcon isOpen={isOpen} />
            </chakra.button>
            <chakra.div
                zIndex="1"
                width="100%"
                {...mergeWith(getPopperProps(), {
                    style: { visibility: isOpen ? 'visible' : 'hidden' },
                })}>
                <chakra.ul
                    __css={styles.menu}
                    data-focus-visible-added={isOpen}
                    {...getMenuProps()}>
                    {isOpen &&
                        validChildren.map((item, index) =>
                            cloneElement(item, {
                                __css: styles.option,
                                ...getItemProps({
                                    item: item.props.value,
                                    index,
                                }),
                            }),
                        )}
                </chakra.ul>
            </chakra.div>
        </chakra.div>
    );
});

CustomSelect.displayName = 'CustomSelect';

export { CustomSelect, Option };
